import * as React from "react"
import { cn } from "@/lib/utils"

export interface ConfirmationModalProps {
  /**
   * The message to display in the modal
   */
  message: string
  /**
   * Text for the cancel/no button
   * @default "CANCEL"
   */
  cancelText?: string
  /**
   * Text for the confirm/yes button
   * @default "CONFIRM"
   */
  confirmText?: string
  /**
   * Callback when cancel button is clicked
   */
  onCancel: () => void
  /**
   * Callback when confirm button is clicked
   */
  onConfirm: () => void
  /**
   * Additional CSS classes for the modal container
   */
  className?: string
}

/**
 * Renders markdown-formatted message text.
 * Supports **bold** syntax only for backward compatibility.
 *
 * @param message - Plain text or text with **bold** markdown
 * @returns Array of React nodes with proper formatting
 */
function renderMessageWithMarkdown(message: string): React.ReactNode {
  // Simple regex to match **text** for bold
  const boldPattern = /\*\*(.+?)\*\*/g
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  // Find all bold markdown patterns
  while ((match = boldPattern.exec(message)) !== null) {
    // Add text before the bold part
    if (match.index > lastIndex) {
      parts.push(message.substring(lastIndex, match.index))
    }
    // Add bold text
    parts.push(
      <strong key={match.index} className="font-bold">
        {match[1]}
      </strong>
    )
    lastIndex = match.index + match[0].length
  }

  // Add remaining text after last bold part
  if (lastIndex < message.length) {
    parts.push(message.substring(lastIndex))
  }

  // If no markdown found, return original message
  return parts.length > 0 ? parts : message
}

/**
 * Confirmation modal component
 *
 * A simple confirmation dialog with customizable yes/no button text.
 * Follows ANYW3AR Figma design specifications (node-id: 699:4886).
 *
 * Supports **bold** markdown in message text for emphasis.
 *
 * @example
 * ```tsx
 * <ConfirmationModal
 *   message="Are you sure you want to delete your account?"
 *   cancelText="CANCEL"
 *   confirmText="DELETE"
 *   onCancel={() => console.log('cancelled')}
 *   onConfirm={() => console.log('confirmed')}
 * />
 *
 * // With markdown bold text
 * <ConfirmationModal
 *   message="You will need to scan again in **5 minutes** to complete pairing."
 *   cancelText="CANCEL"
 *   confirmText="CONFIRM"
 *   onCancel={() => console.log('cancelled')}
 *   onConfirm={() => console.log('confirmed')}
 * />
 * ```
 */
export const ConfirmationModal = React.forwardRef<HTMLDivElement, ConfirmationModalProps>(
  (
    {
      message,
      cancelText = "CANCEL",
      confirmText = "CONFIRM",
      onCancel,
      onConfirm,
      className,
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col gap-4 items-center justify-center",
          "bg-gray-100 rounded-[0.3125rem] p-4",
          "w-full",
          className
        )}
      >
        {/* Message with markdown support */}
        <p className="font-sf-pro text-[0.9375rem] text-black leading-normal w-full whitespace-pre-wrap">
          {renderMessageWithMarkdown(message)}
        </p>

        {/* Buttons */}
        <div className="flex gap-4 w-full">
          {/* Cancel Button */}
          <button
            onClick={onCancel}
            className={cn(
              "flex-1 flex items-center justify-center",
              "bg-white rounded-[0.375rem]",
              "px-4 py-2 h-[2.3556rem]",
              "font-montserrat font-bold text-[0.9375rem] text-black text-center uppercase",
              "hover:bg-gray-50 active:bg-gray-100",
              "transition-colors"
            )}
          >
            {cancelText}
          </button>

          {/* Confirm Button */}
          <button
            onClick={onConfirm}
            className={cn(
              "flex-1 flex items-center justify-center",
              "bg-gray-900 rounded-[0.375rem]",
              "px-4 py-2 h-[2.3556rem]",
              "font-montserrat font-bold text-[0.9375rem] text-white text-center uppercase",
              "hover:bg-gray-700 active:bg-gray-900",
              "transition-colors"
            )}
          >
            {confirmText}
          </button>
        </div>
      </div>
    )
  }
)

ConfirmationModal.displayName = "ConfirmationModal"
